<script lang="ts">
  /**
   * The weekly digest — what the community asked last week, and what to make about it.
   *
   * REPLACES "Questions & gaps" (`AdminInsightsQuestions.svelte`), which ranked all-time
   * clusters by an opaque `issue_score` and labelled 94% of them a "gap" because coverage
   * was a fixed cosine threshold cutting through the middle of a unimodal distribution.
   * Both pages exist while the new pipeline is judged; the old one goes with the
   * clustering module.
   *
   * ONE WEEK AT A TIME, deliberately. The product is "every Monday you get last week's
   * hot questions", so the default view is one snapshot and the week picker is how you
   * move. `Recurring` is the second tab because "asked six weeks running and you still
   * have no lesson" is the most valuable thing here, and it needs the whole range.
   *
   * IT BORROWS THE PANEL'S OWN PRIMITIVES rather than the old page's. `<StatCard>`,
   * `<SkoolPost>`, `<SkoolComment>`, `<Avatar>` (via `<AvatarStack>`) and `<Sparkline>`
   * are what the rest of the admin renders members and content with, so a question here
   * looks like the same question on the timeline — and inherits private-mode redaction,
   * which those primitives carry and a hand-rolled blockquote would not.
   */
  import { navigate } from '../lib/router.svelte';
  import AdminShell from './AdminShell.svelte';
  import PageContainer from '../lib/ui/PageContainer.svelte';
  import PageHeader from '../lib/ui/PageHeader.svelte';
  import StatCard from '../lib/ui/StatCard.svelte';
  import DigestThemeCard from '../lib/ui/DigestThemeCard.svelte';
  import DigestTopicCard from '../lib/ui/DigestTopicCard.svelte';
  import AskCard from '../lib/ui/AskCard.svelte';
  import { weekLabel } from '../lib/ui/weeks';
  import TopicQuestions from '../lib/ui/TopicQuestions.svelte';
  import ThreadDrawer from '../lib/ui/ThreadDrawer.svelte';
  import StateBlock from '@ui/StateBlock.svelte';
  import { redirectIfAwaitingOnboarding, getToken, handle401, type AdminUser } from '../lib/admin';
  import { withCommunity, onCommunityChange } from '../lib/community.svelte';
  import { asAdminUser, cachedBootstrap, ensureBootstrap } from '../lib/session';
  import {
    fetchDigest,
    fetchDigestWeeks,
    fetchTopics,
    type Digest,
    type DigestQuote,
    type DigestWeek,
    type TopicRollupRow,
  } from '@api';

  let me = $state<AdminUser | null>(null);
  let weeks = $state<DigestWeek[]>([]);
  let digest = $state<Digest | null>(null);
  let topics = $state<TopicRollupRow[]>([]);
  let selectedWeek = $state<string | null>(null);
  let tab = $state<'week' | 'recurring'>('week');
  let openTheme = $state<number | null>(null);
  /** The recurring topic expanded to its questions. One at a time, like a theme. */
  let openTopic = $state<number | null>(null);
  /**
   * The question whose thread is open, and the row that thread focuses on.
   *
   * ONE DRAWER FOR THE WHOLE PAGE rather than one per card: a reader opens threads one
   * at a time, and a drawer per theme would mean nine mounted panels each holding a
   * fetch. `<ThreadDrawer>` is the same panel the timeline opens, so a thread read from
   * here looks like a thread read from there.
   */
  let openAsk = $state<DigestQuote | null>(null);
  // privacy-ok: handed to <ThreadDrawer>, which marks the title it renders
  const askPostTitle = $derived(openAsk?.parent_title ?? openAsk?.title ?? null);
  const askTarget = $derived(
    openAsk
      ? {
          kind: (openAsk.source === 'comment' ? 'comment' : 'post') as 'comment' | 'post',
          id: (openAsk.source === 'comment' ? openAsk.comment_id : openAsk.post_id) ?? 0,
          postId: openAsk.post_id ?? 0,
        }
      : null,
  );
  let loading = $state(true);
  let weekLoading = $state(false);
  let error = $state<string | null>(null);
  let lastId = -1;

  async function fetchAll(id: number): Promise<void> {
    lastId = id;
    const [ws, d, t] = await Promise.all([
      fetchDigestWeeks(id),
      fetchDigest(id, selectedWeek ?? undefined),
      fetchTopics(id, { limit: 60 }),
    ]);
    weeks = ws;
    digest = d;
    topics = t;
    selectedWeek = d.week_start ? d.week_start.slice(0, 10) : null;
  }

  async function pickWeek(week: string): Promise<void> {
    if (lastId < 0 || week === selectedWeek) return;
    selectedWeek = week;
    openTheme = null;
    weekLoading = true;
    try {
      digest = await fetchDigest(lastId, week);
    } finally {
      weekLoading = false;
    }
  }

  async function init(): Promise<void> {
    if (!getToken()) {
      navigate('/admin/login');
      return;
    }
    loading = true;
    error = null;
    try {
      const [boot] = await Promise.all([ensureBootstrap(), withCommunity(fetchAll)]);
      me = asAdminUser(boot);
      if (redirectIfAwaitingOnboarding(me, navigate)) return;
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      const boot = cachedBootstrap();
      if (boot) {
        me = asAdminUser(boot);
        if (redirectIfAwaitingOnboarding(me, navigate)) return;
      }
      error = err instanceof Error ? err.message : String(err);
    } finally {
      loading = false;
    }
  }

  init();
  onCommunityChange(() => {
    selectedWeek = null;
    openTheme = null;
    void withCommunity(fetchAll);
  });


  const themes = $derived(digest?.themes ?? []);
  /** Asked this week, grouped into nothing. See `_ungrouped_for` for why it is served. */
  const ungrouped = $derived(digest?.ungrouped ?? []);
  /** The headline is what to ACT on, not what was asked — the page exists to produce a
   *  to-do list, so the tile that leads is the count of themes the course does not
   *  answer. */
  const needsWork = $derived(themes.filter((t) => t.coverage === 'missing' || t.coverage === 'thin').length);
  const missing = $derived(themes.filter((t) => t.coverage === 'missing').length);
  const longestStreak = $derived(themes.reduce((n, t) => Math.max(n, t.streak_weeks), 0));
  /** Oldest-first, so the volume sparkline reads left-to-right like every other chart. */
  const volumeSpark = $derived([...weeks].reverse().map((w) => w.question_count));
  const volumeLabels = $derived([...weeks].reverse().map((w) => weekLabel(w.week_start)));
  const recurring = $derived(topics.filter((t) => t.weeks_seen > 1));
</script>

<AdminShell {me}>
  {#if loading}
    <StateBlock state="loading" />
  {:else if error && !digest}
    <StateBlock state="error" message={error} onretry={init} />
  {:else}
    <PageContainer>
      <PageHeader section="Insights" title="Weekly digest" updated={digest?.generated_at ?? undefined} />

      {#if !digest?.found}
        <StateBlock
          state="empty"
          message="No digest yet. One is built every Sunday night from the week's questions."
        />
      {:else}
        <!-- ONE ROW. View and Week were stacked, each with a fixed-width label column,
             which spent two rows and a hundred vertical pixels on six words and left
             the right two-thirds of the box empty. They are both "what am I looking
             at", so they sit on one line and wrap only when they have to. -->
        <div class="rounded-box mb-4 border hair bg-base-100 px-4 py-3">
          <div class="flex flex-wrap items-center gap-x-6 gap-y-2">
            <div class="flex items-center gap-2.5">
              <span class="eyebrow shrink-0 text-[0.62rem]">View</span>
              <div class="flex gap-1">
                <button
                  class="rounded-btn px-3 py-1 text-[0.8rem] {tab === 'week'
                    ? 'bg-primary text-primary-content'
                    : 'text-base-content/60 hover:bg-base-200'}"
                  onclick={() => (tab = 'week')}>This week</button
                >
                <button
                  class="rounded-btn px-3 py-1 text-[0.8rem] {tab === 'recurring'
                    ? 'bg-primary text-primary-content'
                    : 'text-base-content/60 hover:bg-base-200'}"
                  onclick={() => (tab = 'recurring')}>Recurring · {recurring.length}</button
                >
              </div>
            </div>
            {#if tab === 'week'}
              <div class="flex min-w-0 items-center gap-2.5">
                <span class="eyebrow shrink-0 text-[0.62rem]">Week</span>
                <div class="flex flex-wrap gap-1">
                  {#each weeks.slice(0, 14) as w (w.week_start)}
                    <button
                      class="rounded-full px-2.5 py-1 font-mono text-[0.68rem] transition-colors
                             {selectedWeek === w.week_start.slice(0, 10)
                        ? 'bg-primary/15 text-primary'
                        : 'text-base-content/45 hover:bg-base-200 hover:text-base-content'}"
                      onclick={() => pickWeek(w.week_start.slice(0, 10))}
                      title="{w.question_count} questions · {w.theme_count} themes"
                    >
                      {weekLabel(w.week_start)}
                    </button>
                  {/each}
                </div>
              </div>
            {/if}
          </div>
        </div>

        {#if tab === 'week'}
          <div class="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
            <!-- "6" over ten cards read as an unexplained number. It is a SHARE of the
                 themes below, so it says so; the sub-line then spends its words on the
                 harder half — how many have no lesson behind them at all. -->
            <!-- WITH NO THEMES THERE IS NOTHING TO ACT ON, and saying "thin coverage —
                 a lesson exists to build on" under a dash was advice about themes that
                 do not exist. -->
            <StatCard
              label="To act on"
              value={themes.length ? `${needsWork}/${themes.length}` : '—'}
              sub={!themes.length
                ? 'nothing to build on this week'
                : missing > 0
                  ? `${missing} with no lesson at all`
                  : 'thin coverage — a lesson exists to build on'}
              tone={themes.length && missing > 0 ? 'warning' : 'default'}
            />
            <StatCard label="Themes" value={String(themes.length)} sub="from this week's questions" />
            <StatCard
              label="Questions"
              value={String(digest.question_count ?? 0)}
              sub="asked by members"
              spark={volumeSpark}
              sparkLabels={volumeLabels}
              sparkFormat={(v) => `${v} questions`}
            />
            <StatCard
              label="Longest run"
              value={longestStreak > 1 ? `${longestStreak} wks` : '—'}
              sub={longestStreak > 1 ? 'same topic, still asked' : 'nothing recurring yet'}
              tone={longestStreak >= 3 ? 'warning' : 'default'}
            />
          </div>

          {#if weekLoading}
            <StateBlock state="loading" />
          {:else if !themes.length}
            <!-- THE NUMBER AND THE EMPTINESS HAVE TO RECONCILE. The tile above says
                 "11 questions"; a bare "nobody asked anything" beside it reads as a
                 contradiction and leaves no way to check the judgement. So the copy
                 names the count it is explaining, and the questions themselves are
                 listed underneath — on a community that converses rather than asks,
                 seeing "What kind of sauce?" is the answer. -->
            <StateBlock
              state="empty"
              message={ungrouped.length
                ? `${ungrouped.length} question${ungrouped.length === 1 ? '' : 's'} asked, none of them about your material — they are below.`
                : 'Nobody asked anything this week.'}
            />
            {#if ungrouped.length}
              <div class="mt-3 space-y-2">
                {#each ungrouped as q (q.question_id)}
                  <AskCard quote={q} onopen={() => (openAsk = q)} />
                {/each}
              </div>
            {/if}
          {:else}
            <div class="space-y-3">
              {#each themes as t (t.topic_id)}
                <DigestThemeCard
                  theme={t}
                  open={openTheme === t.topic_id}
                  ontoggle={() => (openTheme = openTheme === t.topic_id ? null : t.topic_id)}
                  onopenthread={(q) => (openAsk = q)}
                />
              {/each}
            </div>
          {/if}
        {:else}
          <p class="mb-4 max-w-prose text-[0.86rem] text-base-content/60">
            Topics that keep coming back, across every week held. A topic here for several
            weeks is a standing gap in the course, not a one-off question.
          </p>
          {#if !recurring.length}
            <StateBlock
              state="empty"
              message="Nothing has repeated across weeks yet — it takes two digests to see a run."
              compact
            />
          {:else}
            <!-- CARDS, NOT A TABLE. Six numeric columns made the two tabs read as two
                 different products — cards on one, a spreadsheet on the other — for the
                 same object answering the same question. `<DigestTopicCard>` is the
                 week card's twin: same rail, same badge, same sparkline geometry, so a
                 shape means the same thing whichever tab you are in. -->
            <div class="space-y-3">
              {#each recurring as t (t.topic_id)}
                <DigestTopicCard
                  topic={t}
                  open={openTopic === t.topic_id}
                  ontoggle={() => (openTopic = openTopic === t.topic_id ? null : t.topic_id)}
                >
                  <TopicQuestions
                    skoolId={lastId}
                    topicId={t.topic_id}
                    onopenthread={(q) => (openAsk = q)}
                  />
                </DigestTopicCard>
              {/each}
            </div>
          {/if}
        {/if}
      {/if}
    </PageContainer>
    <ThreadDrawer
      open={openAsk != null}
      onclose={() => (openAsk = null)}
      skoolId={lastId}
      target={askTarget}
      eyebrow={openAsk?.source === 'comment' ? 'Question asked in this thread' : 'Question'}
      postTitle={askPostTitle}
      skoolUrl={openAsk?.permalink ?? null}
    />
  {/if}
</AdminShell>
